import { Fragment, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { Section } from "@/components/site-blocks";
import { CaseStudyHero } from "@/components/portfolio/case-study-hero";
import { ConversionBanner } from "@/components/portfolio/conversion-banner";
import { FinalSolarCta } from "@/components/portfolio/final-solar-cta";
import { InstallationCard } from "@/components/portfolio/installation-card";
import {
  DEFAULT_FILTERS,
  InstallationFilters,
  type FilterState,
} from "@/components/portfolio/installation-filters";
import { Reveal } from "@/components/portfolio/reveal";
import { TrustStrip } from "@/components/portfolio/trust-strip";
import {
  hasBeforeAndAfter,
  installSortKey,
  installYear,
  matchesSizeBand,
  fetchProperties,
} from "@/lib/portfolio";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/properties/")({
  head: () => ({
    ...pageSeo({
      path: "/properties",
      title: "Solar Installations We've Completed | CRS Energy",
      description:
        "Browse real solar PV installations completed by CRS Energy across Wales and the South West. See system sizes, battery storage, generation figures and photos.",
    }),
  }),
  loader: () => fetchProperties(),
  component: PortfolioIndexPage,
});

const FILTERS_ID = "find-a-home-like-yours";

function PortfolioIndexPage() {
  const properties = Route.useLoaderData();
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);

  const years = useMemo(
    () =>
      Array.from(
        new Set(properties.map((p) => installYear(p.install_date)).filter(Boolean) as string[]),
      ).sort((a, b) => Number(b) - Number(a)),
    [properties],
  );

  const filtered = useMemo(() => {
    return properties
      .filter((property) => {
        if (filters.houseType !== "all" && property.house_type !== filters.houseType) return false;
        if (filters.system === "battery" && !property.has_battery) return false;
        if (filters.system === "solar" && property.has_battery) return false;
        if (!matchesSizeBand(property.system_size_kwp, filters.size)) return false;
        if (filters.year !== "any" && installYear(property.install_date) !== filters.year)
          return false;
        if (filters.beforeAfterOnly && !hasBeforeAndAfter(property.images)) return false;
        return true;
      })
      .sort((a, b) => installSortKey(b.install_date) - installSortKey(a.install_date));
  }, [properties, filters]);

  return (
    <>
      <CaseStudyHero properties={properties} filtersId={FILTERS_ID} />
      <TrustStrip />

      <Section className="pb-10 md:pb-12">
        <InstallationFilters
          id={FILTERS_ID}
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(DEFAULT_FILTERS)}
          years={years}
          resultCount={filtered.length}
        />
      </Section>

      <Section className="pt-0">
        {filtered.length === 0 ? (
          <div className="rounded-[24px] border border-dashed border-border bg-crs-blue-pale p-10 text-center">
            <p className="text-lg font-semibold text-foreground">
              No installations match those filters yet.
            </p>
            <p className="mt-2 text-muted-foreground">
              Try widening your search, or ask our team what would suit your home.
            </p>
            <button
              type="button"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="mt-6 inline-flex min-h-12 items-center justify-center rounded-full bg-crs-blue px-6 text-base font-semibold text-white shadow-soft"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((property, index) => (
              <Fragment key={property.reference_code}>
                <Reveal as="li" delayMs={(index % 3) * 80} className="h-full">
                  <InstallationCard property={property} className="h-full" />
                </Reveal>
                {index === 2 && filtered.length > 3 && (
                  <li className="sm:col-span-2 lg:col-span-3">
                    <ConversionBanner />
                  </li>
                )}
              </Fragment>
            ))}
          </ul>
        )}
      </Section>

      <FinalSolarCta />
    </>
  );
}
